/**
 * The category sidebar.
 *
 * Draws itself from `navigation.ts`, filtered by what the caller may reach. A
 * section whose every item is hidden disappears with its heading — a lone
 * heading over nothing looks like a rendering fault.
 */
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';
import { getHealth } from '../api/health';
import { Logo } from '../components/Logo';
import { useAuth } from '../auth/useAuth';
import { useAsync } from '../hooks/useAsync';
import { GLASS_RADIUS, SHELL_GAP, TOP_BAR_HEIGHT, glass } from '../theme/theme';
import { MODULE_NAMESPACES } from '../modules/locales';
import { formatRelease } from '../utils/format';
import { useModules } from '../modules/useModules';
import { isCurrent, navigationWith } from './navigation';

export const SIDEBAR_WIDTH = 256;

/**
 * One block of the sidebar: a section, or the release line.
 *
 * Attached, it is rounded on the left only and flush with the drawer's right
 * edge, which is where the main panel begins — so each group reads as a tab
 * attached to it. Detached, in the drawer that slides over the page, there is
 * no panel to attach to: the same gap and the same corners on every side.
 */
function groupSx(attached: boolean) {
  return (theme: Theme) => ({
    ...glass(theme, 'group'),
    ml: SHELL_GAP,
    mr: attached ? 0 : SHELL_GAP,
    mb: SHELL_GAP,
    borderRadius: attached
      ? `${GLASS_RADIUS}px 0 0 ${GLASS_RADIUS}px`
      : `${GLASS_RADIUS}px`,
  });
}

export interface SidebarProps {
  /**
   * Whether the groups attach to the main panel beside the sidebar: true in
   * the permanent drawer, false in the one that slides over the page.
   */
  attached: boolean;
  /** Closes the temporary drawer after navigating. No-op on a wide viewport. */
  onNavigate?: () => void;
}

export function Sidebar({ attached, onNavigate }: SidebarProps) {
  // Module namespaces too: a module page's menu label is `crm:nav.…`, and a
  // section a module declares is titled in its namespace.
  const { t } = useTranslation(['nav', 'common', ...MODULE_NAMESPACES]);
  const { canAny } = useAuth();
  const { pathname } = useLocation();
  const modules = useModules();
  // Once, not on a timer: the build changes only with a deploy, and a stale
  // line in a tab left open across one is harmless. A failure shows nothing —
  // the health banner is what reports a backend that is down.
  const health = useAsync(() => getHealth(), []);

  const sections = navigationWith(modules).map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.anyOf || canAny(...item.anyOf)),
  })).filter((section) => section.items.length > 0);

  const group = groupSx(attached);
  // Attached, the groups keep clear of the main panel's rounded top corner:
  // they start a corner radius below its top, where its edge is straight.
  // There is no corner clearance at the bottom — what sits there is the
  // release line, which is detached and attaches to nothing. Detached, there
  // are no corners to keep clear of.
  const cornerClearance = attached ? `${GLASS_RADIUS}px` : 0;

  return (
    // Ends a `SHELL_GAP` above the screen's bottom, where the main panel ends,
    // so the scrollbar stops level with the panel rather than at the edge.
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', pb: SHELL_GAP }}>
      {/* Exactly as tall as the top bar, so the logo block and the header meet
          in one unbroken line across the drawer edge. */}
      <Box
        sx={{
          px: 2.5,
          height: TOP_BAR_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <Logo size={34} withWordmark subtitle={t('common:product.tagline')} />
      </Box>

      {/* A margin, not padding: a scrolled group is clipped here rather than
          sliding into the corner. A flex column so the release line below can
          take the free space above itself — see there. */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          mt: cornerClearance,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {sections.map((section) => (
          <List
            key={section.id}
            dense
            subheader={
              <ListSubheader
                disableSticky
                sx={{
                  bgcolor: 'transparent',
                  color: 'text.secondary',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  textAlign: 'center',
                  lineHeight: 2.5,
                }}
              >
                {t(section.titleKey)}
              </ListSubheader>
            }
            // No shrinking: in a flex column a group would squash to fit
            // rather than overflow, and the box would never scroll.
            sx={[group, { px: 1.5, py: 0.5, flexShrink: 0 }]}
          >
            {section.items.map((item) => {
              const selected = isCurrent(item, pathname);
              const Icon = item.icon;

              return (
                <ListItemButton
                  key={item.to}
                  component={NavLink}
                  to={item.to}
                  end={item.exact ?? false}
                  selected={selected}
                  onClick={onNavigate}
                  sx={{
                    borderRadius: 2,
                    mb: 0.25,
                    // The selected row carries the brand colour; everything else
                    // stays quiet, so one glance finds where you are.
                    '&.Mui-selected': {
                      bgcolor: 'action.selected',
                      color: 'primary.main',
                      '& .MuiListItemIcon-root': { color: 'primary.main' },
                      '&:hover': { bgcolor: 'action.selected' },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                    <Icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t(item.labelKey)}
                    slotProps={{
                      // Typography props only — the size and weight are styles,
                      // so they travel through sx rather than as bare props.
                      primary: { sx: { fontSize: 14, fontWeight: selected ? 600 : 500 } },
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        ))}

        {/* Which build is running, for everybody, in the faintest text
            colour: there to be found when somebody asks, not to be read on
            every visit. On group glass, because on the bare photograph the
            light theme's faint text is lost; detached and only as wide as its
            text, so it does not read as one more section. The auto margin
            takes whatever space the groups leave, so with room to spare it
            sits at the bottom, and when the groups overflow it is zero and the
            line follows the last group, scrolling with it. */}
        {health.data && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={[
              groupSx(false),
              // No bottom margin: the sidebar's own padding is the gap below.
              // Centred under the groups rather than the drawer: the same side
              // margins as theirs, so it lines up with the section headings.
              {
                mt: 'auto',
                mb: 0,
                mr: attached ? 0 : SHELL_GAP,
                px: 1.5,
                py: 0.5,
                alignSelf: 'center',
                flexShrink: 0,
                fontSize: '0.65rem',
              },
            ]}
          >
            {/* Opacity on an inner span, so it fades the text and not the glass
                behind it. */}
            <Box component="span" sx={{ opacity: 0.75 }}>
              {formatRelease(health.data.release)}
            </Box>
          </Typography>
        )}
      </Box>
    </Box>
  );
}
